export type BillingPeriodKey = "Q1" | "Q2" | "Q3" | "Q4" | "WY";

export type BillingReportDateRange = {
  accountingPeriodId: string;
  periodKey: BillingPeriodKey;
  fixedFromDate: string;
  fixedToDate: string;
  varFromDate: string;
  varToDate: string;
};

export type BillingReportDateRangeOption = {
  label: string;
  value: BillingReportDateRange;
};

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ISO_DATE_PREFIX_PATTERN = /^(\d{4}-\d{2}-\d{2})/;

// Take the calendar date from the API's ISO string verbatim (the backend's
// TZ env already produces it in the right zone). Avoid `new Date(...)` here
// because it would convert to a UTC instant and lose the offset.
const toDateOnly = (value: string | Date): string => {
  if (typeof value === "string") {
    const match = value.match(ISO_DATE_PREFIX_PATTERN);
    if (match) {
      return match[1];
    }
  }
  if (value instanceof Date) {
    return [
      value.getFullYear(),
      String(value.getMonth() + 1).padStart(2, "0"),
      String(value.getDate()).padStart(2, "0"),
    ].join("-");
  }
  return String(value).slice(0, 10);
};

const fromDateOnly = (dateOnly: string): Date => {
  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const addMonths = (dateOnly: string, months: number): string => {
  const date = fromDateOnly(dateOnly);
  date.setUTCMonth(date.getUTCMonth() + months);
  return toDateOnly(date);
};

const addDays = (dateOnly: string, days: number): string => {
  const date = fromDateOnly(dateOnly);
  date.setUTCDate(date.getUTCDate() + days);
  return toDateOnly(date);
};

export const formatBillingDateOnly = (
  dateOnly: string,
  formatString = "dd/MM/yyyy",
): string => {
  const date = fromDateOnly(dateOnly);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const dd = String(day).padStart(2, "0");
  const mm = String(month + 1).padStart(2, "0");
  const yyyy = String(year);
  const yy = yyyy.slice(-2);
  const mmm = MONTH_SHORT[month];

  switch (formatString) {
    case "yyyyMMdd":
      return `${yyyy}${mm}${dd}`;
    case "MMMyy":
      return `${mmm}${yy}`;
    case "MMM yyyy":
      return `${mmm} ${yyyy}`;
    case "dd/MM/yyyy":
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
};

export const getBillingReportDateRangeOptions = (
  accountingPeriods: any[] = [],
): BillingReportDateRangeOption[] => {
  const uniquePeriods: Record<string, any> = {};
  accountingPeriods.forEach(period => {
    const uniqueKey = `${period.periodStart}-${period.periodEnd}`;
    if (!uniquePeriods[uniqueKey]) {
      uniquePeriods[uniqueKey] = period;
    }
  });

  return Object.values(uniquePeriods).flatMap(accountingPeriod => {
    const periodStart = toDateOnly(accountingPeriod.periodStart);
    const periodEnd = addDays(addMonths(periodStart, 12), -1);
    // "WY2026" = the water year ending in 2026 (i.e. periodEnd's year).
    const waterYear = fromDateOnly(periodEnd).getUTCFullYear();
    const options: BillingReportDateRangeOption[] = [];

    for (let i = 0; i < 4; i++) {
      const periodKey = `Q${i + 1}` as BillingPeriodKey;
      const varFromDate = addMonths(periodStart, i * 3);
      const varToDate = addDays(addMonths(periodStart, (i + 1) * 3), -1);
      const fixedFromDate = addMonths(periodStart, (i + 1) * 3);
      const fixedToDate = addDays(addMonths(fixedFromDate, 3), -1);

      options.push({
        label: `${periodKey} WY${waterYear} (${formatBillingDateOnly(
          varFromDate,
        )} - ${formatBillingDateOnly(varToDate)})`,
        value: {
          accountingPeriodId: accountingPeriod.id,
          periodKey,
          fixedFromDate,
          fixedToDate,
          varFromDate,
          varToDate,
        },
      });
    }

    const fixedFromDate = addMonths(periodStart, 12);
    const fixedToDate = addDays(addMonths(fixedFromDate, 3), -1);

    options.push({
      label: `Water Year ${waterYear} (${formatBillingDateOnly(
        periodStart,
      )} - ${formatBillingDateOnly(periodEnd)})`,
      value: {
        accountingPeriodId: accountingPeriod.id,
        periodKey: "WY",
        fixedFromDate,
        fixedToDate,
        varFromDate: periodStart,
        varToDate: periodEnd,
      },
    });

    return options;
  });
};
